import { useEffect, useState } from "react";
import { rgApi } from "@/lib/rgApi";

/**
 * Bloc Réglages : Connexion privée au calendrier iCloud (CalDAV lecture seule).
 * - Import uniquement des événements titrés "OFF" et des autres événements anonymisés en PERSO.
 * - Mot de passe d'application Apple stocké chiffré côté serveur, jamais renvoyé au frontend.
 */
export default function ICloudSyncBlock() {
  const [status, setStatus] = useState(null);
  const [appleId, setAppleId] = useState("");
  const [appPassword, setAppPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async () => {
    try { setStatus(await rgApi.icloudStatus()); }
    catch (_e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const flash = (msg, type = "info") => {
    setMessage({ msg, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const saveAndTest = async () => {
    if (!appleId.trim() || !appPassword.trim()) {
      flash("Renseigne Apple ID et mot de passe d'application", "warn");
      return;
    }
    setBusy(true);
    try {
      await rgApi.icloudSetCredentials(appleId.trim(), appPassword.trim());
      const r = await rgApi.icloudTest();
      flash(`Connexion réussie · ${(r.calendars || []).length} calendrier(s) trouvé(s)`, "ok");
      setAppPassword("");
      await load();
    } catch (e) {
      flash(e.data?.detail || "Échec de la connexion", "error");
    } finally { setBusy(false); }
  };

  const testAgain = async () => {
    setBusy(true);
    try {
      const r = await rgApi.icloudTest();
      flash(`Connexion OK · ${(r.calendars || []).length} calendrier(s) disponibles`, "ok");
      await load();
    } catch (e) {
      flash(e.data?.detail || "Échec du test", "error");
    } finally { setBusy(false); }
  };

  const toggleCalendar = async (url) => {
    const selected = status.selected_calendars || [];
    const next = selected.includes(url)
      ? selected.filter((u) => u !== url)
      : [...selected, url];
    setBusy(true);
    try {
      await rgApi.icloudSetCalendars(next);
      await load();
    } finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await rgApi.icloudSync();
      const s = r.stats || {};
      flash(`Synchro terminée · OFF ${s.OFF || 0} · PERSO ${s.PERSO || 0} · exclus ${s.excluded || 0} · purgés ${s.purged || 0}`, "ok");
      await load();
    } catch (e) {
      flash(e.data?.detail || "Échec de la synchro", "error");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter iCloud et supprimer les événements importés ?")) return;
    setBusy(true);
    try {
      await rgApi.icloudDisconnect();
      flash("iCloud déconnecté", "ok");
      await load();
    } finally { setBusy(false); }
  };

  if (!status) return null;

  const connected = status.connected;
  const available = status.available_calendars || [];
  const selected = status.selected_calendars || [];

  return (
    <div className="rg-card" data-testid="rg-icloud-block">
      <h3 className="rg-h3">📅 Calendrier personnel iCloud</h3>
      <p className="rg-muted" style={{ fontSize: 12, marginTop: -4 }}>
        Import privé (lecture seule) : les événements titrés « OFF » deviennent des jours de fermeture Refuge,
        les autres apparaissent anonymisés en <code>PERSO · HH:mm</code>. Aucune donnée n'est envoyée vers iCloud.
      </p>

      <details style={{ marginTop: 12, fontSize: 12 }}>
        <summary style={{ cursor: "pointer", fontWeight: 600 }}>💡 Comment créer un mot de passe d'application Apple ?</summary>
        <ol style={{ margin: "8px 0 0 20px", lineHeight: 1.5 }}>
          <li>Va sur <a href="https://appleid.apple.com" target="_blank" rel="noreferrer">appleid.apple.com</a> → Connexion</li>
          <li>Section « Connexion et sécurité » → « Mots de passe d'application »</li>
          <li>Génère un nouveau mot de passe (nom au choix, ex. « Curieux Hub »)</li>
          <li>Copie le mot de passe généré (format <code>xxxx-xxxx-xxxx-xxxx</code>)</li>
          <li>Colle-le ici — jamais ton mot de passe Apple principal</li>
        </ol>
      </details>

      {!connected ? (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <label className="rg-tiny">Apple ID (email)</label>
          <input
            type="email"
            className="rg-input"
            placeholder="prenom.nom@icloud.com"
            value={appleId}
            onChange={(e) => setAppleId(e.target.value)}
            autoComplete="off"
            data-testid="rg-icloud-apple-id"
          />
          <label className="rg-tiny">Mot de passe d'application (16 caractères)</label>
          <input
            type="password"
            className="rg-input"
            placeholder="xxxx-xxxx-xxxx-xxxx"
            value={appPassword}
            onChange={(e) => setAppPassword(e.target.value)}
            autoComplete="new-password"
            data-testid="rg-icloud-app-password"
          />
          <div className="rg-row" style={{ gap: 8 }}>
            <button className="rg-btn" onClick={saveAndTest} disabled={busy} data-testid="rg-icloud-connect">
              Connecter & Tester
            </button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          <div className="rg-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontSize: 12 }}>
                ✅ Connecté avec <strong data-testid="rg-icloud-id-masked">{status.apple_id_masked}</strong>
              </div>
              {status.last_sync_at && (
                <div className="rg-tiny" style={{ color: "var(--rg-text-3)", marginTop: 2 }}>
                  Dernière synchro : {new Date(status.last_sync_at).toLocaleString("fr-FR", { timeZone: "Indian/Reunion" })}
                  {status.last_sync_ok === false ? " · ❌ échec" : ""}
                </div>
              )}
              {status.last_sync_stats && Object.keys(status.last_sync_stats).length > 0 && (
                <div className="rg-tiny" style={{ color: "var(--rg-text-3)" }}>
                  OFF {status.last_sync_stats.OFF || 0} · PERSO {status.last_sync_stats.PERSO || 0} · exclus {status.last_sync_stats.excluded || 0} · purgés {status.last_sync_stats.purged || 0}
                </div>
              )}
              {status.last_sync_error && (
                <div className="rg-tiny" style={{ color: "var(--rg-error)", marginTop: 2 }}>
                  {status.last_sync_error}
                </div>
              )}
            </div>
            <div className="rg-row" style={{ gap: 6 }}>
              <button className="rg-btn ghost small" onClick={testAgain} disabled={busy} data-testid="rg-icloud-test">Tester</button>
              <button className="rg-btn small" onClick={syncNow} disabled={busy || selected.length === 0} data-testid="rg-icloud-sync">
                Synchroniser maintenant
              </button>
              <button className="rg-btn ghost small" onClick={disconnect} disabled={busy} data-testid="rg-icloud-disconnect">Déconnecter</button>
            </div>
          </div>

          {available.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div className="rg-tiny" style={{ marginBottom: 6, fontWeight: 600 }}>
                Calendriers à importer ({selected.length}/{available.length} sélectionnés)
              </div>
              <div style={{ display: "grid", gap: 4 }}>
                {available.map((cal) => (
                  <label key={cal.url} className={`rg-prep-row ${selected.includes(cal.url) ? "done" : ""}`} style={{ cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={selected.includes(cal.url)}
                      onChange={() => toggleCalendar(cal.url)}
                      disabled={busy}
                      data-testid={`rg-icloud-cal-${btoa(cal.url).slice(0, 8)}`}
                    />
                    <span style={{ marginLeft: 8 }}>{cal.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {message && (
        <div
          className={`rg-tiny rg-alert-${message.type === "ok" ? "ok" : message.type === "error" ? "error" : "warn"}`}
          style={{ marginTop: 10 }}
          data-testid="rg-icloud-message"
        >
          {message.msg}
        </div>
      )}
    </div>
  );
}
